import { SEND_REQUEST_STATUS } from '../components/customUtils/AppConstants'
import { captureAndSaveDataError } from './errorUtils';

const cache: Map<string, { data: any; timestamp: number }> = new Map()

const validateUrl = (url: string): boolean => {
    try {
        new URL(url) // Intenta crear una URL válida
        return true
    } catch (error) {
        console.error('URL no válida:', url)
        return false
    }
}

const validateHeaders = (headers: object): boolean => {
    if (typeof headers !== 'object' || headers === null) {
        console.error('Headers deben ser un objeto.')
        return false
    }
    return true
}

const validateBody = (body?: object): boolean => {
    if (body && typeof body !== 'object') {
        console.error('El cuerpo de la solicitud debe ser un objeto.')
        return false
    }
    return true
}

const validateTimeout = (Timeout: number): boolean => {
    if (typeof Timeout !== 'number' || Timeout <= 0) {
        console.error('El valor de timeout debe ser un número positivo.')
        return false
    }
    return true
}

const validateRetries = (retries: number): boolean => {
    if (typeof retries !== 'number' || retries < 0) {
        console.error('El valor de retries debe ser un número positivo o 0.')
        return false
    }
    return true
}

// Validación de la estructura de la respuesta
const validateResponseData = (data: any, expectedKeys: string[]): boolean => {
    if (!data) {
        console.error('No se recibió datos en la respuesta')
        return false
    }
    for (const key of expectedKeys) {
        if (!(key in data)) {
            console.error(`Falta la clave esperada: ${key}`)
            return false
        }
    }
    return true
}

/**
 * 
 * @param url URL de la solicitud
 * @param method Método HTTP (GET, POST, PUT, DELETE)
 * @param headers Encabezados de la solicitud
 * @param body Cuerpo de la solicitud
 * @param retries Número de reintentos en caso de fallo
 * @param timeout Tiempo de espera para la solicitud
 * @param customCacheKey Clave personalizada para la caché
 * @param expectedResponseKeys Claves esperadas en la respuesta
 * @param useCache Indica si se debe utilizar la caché
 * @returns 
 */
export const sendRequest = async (
    url: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    headers: object,
    body?: object,
    retries: number = 0, // Por defecto no reintenta
    timeout: number = 60,
    customCacheKey?: string, // Permitir pasar una clave personalizada para cachear
    expectedResponseKeys: string[] = [], // Validar claves esperadas en la respuesta
    useCache: boolean = true // Permitir desactivar el uso de caché
) => {
    // Validación de la URL
    if (!validateUrl(url)) {
        throw new Error('URL inválida.')
    }

    // Validación de headers
    if (!validateHeaders(headers)) {
        throw new Error('Headers inválidos.')
    }

    // Validación del cuerpo de la solicitud
    if (body && !validateBody(body)) {
        throw new Error('Cuerpo de solicitud inválido.')
    }

    // Validación de timeout
    if (!validateTimeout(timeout)) {
        throw new Error('timeout inválido.')
    }

    // Validación de retries
    if (retries < 0) {
        throw new Error('El valor de retries debe ser 0 o un número positivo.')
    }

    // Filtrar headers y construir una nueva clave de caché
    const filteredHeaders = Object.entries(headers || {})
        .filter(([key]) => key.toLowerCase() !== 'authorization')
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

    // Construir la clave sin incluir el token de autorización
    const cacheKey = customCacheKey || `${url}-${JSON.stringify(filteredHeaders)}-${JSON.stringify(body)}`

    // const cacheKey = customCacheKey || `${url}-${JSON.stringify(headers)}-${JSON.stringify(body)}`
    const currentTime = Date.now()
    //console.log('🚀 ~ cacheKey:', cacheKey)

    // 1. Comprobar si la respuesta está en caché y si no ha caducado
    if (cache.has(cacheKey)) {
        const cacheEntry = cache.get(cacheKey)
        // dura 1 m en cache
        if (cacheEntry && currentTime - cacheEntry.timestamp < 60000 && useCache) {
            console.log('Cargando desde cache, ', cacheKey)
            return {
                status: SEND_REQUEST_STATUS.OK,
                response: cacheEntry.data,
                duration: 0,
                retries: 0,
            }
        } else {
            // Si la cache ha caducado, eliminarla
            cache.delete(cacheKey)
        }
    }
    // else {
    //     // Insertar un marcador vacío en la caché para evitar múltiples solicitudes simultáneas
    //     cache.set(cacheKey, { data: null, timestamp: currentTime })
    // }

    // 2. Crear un controlador de aborto para manejar el timeout
    const controller = new AbortController()
    const timeoutInMilliseconds = timeout * 1000 //60000 ahora en segundos
    const timeoutId = setTimeout(() => controller.abort(), timeoutInMilliseconds)

    let attempt = 0
    let startTime = Date.now() // Tiempo de inicio de la solicitud
    const maxTotalTime = timeoutInMilliseconds * retries
    let totalElapsedTime = 0

    // Si el número de reintentos es mayor que 0, entramos en el bucle de reintentos
    while (retries > 0 && attempt < retries) {
        try {
            if (totalElapsedTime >= maxTotalTime) {
                console.error('Tiempo total de reintentos agotado.')
                throw new Error('Tiempo total de reintentos agotado.')
            }
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    ...headers,
                },
                body: body ? JSON.stringify(body) : undefined,
                signal: controller.signal,
            })

            // Limpiar timeout
            clearTimeout(timeoutId)

            if (response.ok) {
                const data = await response.json()

                // Validación de la respuesta
                if (expectedResponseKeys.length > 0 && !validateResponseData(data, expectedResponseKeys)) {
                    throw new Error('La respuesta no contiene las claves esperadas.')
                }

                // 3. Almacenar la respuesta en caché con un timestamp
                cache.set(cacheKey, { data, timestamp: currentTime })

                let endTime = Date.now() // Tiempo de fin
                const duration = (endTime - startTime) / 1000
                console.log(`1Tiempo total de la solicitud: ${duration} s`, url)

                return {
                    status: SEND_REQUEST_STATUS.OK,
                    response: data,
                    duration,
                    retries: attempt,
                }
            } else {
                const errorText = await response.text()
                throw new Error(`Error: ${response.status} - ${response.statusText}: ${errorText}`)
            }
        } catch (error) {
            if (attempt === retries - 1) {
                // Si hemos alcanzado el último intento, lanzamos el error
                console.error('Error final después de reintentos:', error)
                let endTime = Date.now() // Tiempo de fin
                const duration = (endTime - startTime) / 1000
                console.log(`2Tiempo total de la solicitud: ${duration} s`, url)
                const additional = {
                    method,
                    body,
                    retries,
                    timeout,
                    customCacheKey,
                    expectedResponseKeys,
                }

                const dataCapture = {
                    log: error.message,
                    date: new Date().toISOString(),
                    end: url,
                    additional: JSON.stringify(additional)
                }
                captureAndSaveDataError(dataCapture)

                return {
                    status: SEND_REQUEST_STATUS.ERROR,
                    error: error.message,
                    duration,
                    retries: attempt + 1,
                    response: null,
                }
            } else {
                const attemptDuration = Date.now() - startTime
                totalElapsedTime += attemptDuration
                // Si no es el último intento, esperamos un poco antes de reintentar
                console.log(`Intento ${attempt + 1} fallido. Reintentando...`)
                attempt++
                await new Promise((resolve) => setTimeout(resolve, 1000)) // Espera 1 segundo antes de reintentar
            }
        }
    }

    // Si retries es 0 o si no es necesario reintentar, intentamos solo una vez
    try {
        const response = await fetch(url, {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
            body: body ? JSON.stringify(body) : undefined,
            signal: controller.signal,
        })

        // Limpiar timeout
        clearTimeout(timeoutId)

        if (response.ok) {
            const data = await response.json()

            // Validación de la respuesta
            if (expectedResponseKeys.length > 0 && !validateResponseData(data, expectedResponseKeys)) {
                throw new Error('La respuesta no contiene las claves esperadas.')
            }

            // 3. Almacenar la respuesta en caché con un timestamp
            cache.set(cacheKey, { data, timestamp: currentTime })

            let endTime = Date.now() // Tiempo de fin
            const duration = (endTime - startTime) / 1000
            console.log(`3Tiempo total de la solicitud: ${duration} s`, url)

            return {
                status: SEND_REQUEST_STATUS.OK,
                response: data,
                duration,
                retries: attempt,
            }
        } else {
            const errorText = await response.text()
            throw new Error(`Error: ${response.status} - ${response.statusText}: ${errorText}`)
        }
    } catch (error) {
        console.error('Error de solicitud:', error)
        let endTime = Date.now() // Tiempo de fin
        const duration = (endTime - startTime) / 1000
        console.log(`4Tiempo total de la solicitud: ${duration} s`, url)

        const additional = {
            method,
            body,
            retries,
            timeout,
            customCacheKey,
            expectedResponseKeys
        }
        const dataCapture = {
            log: error.message,
            date: new Date().toISOString(),
            end: url,
            additional: JSON.stringify(additional)
        }

        captureAndSaveDataError(dataCapture)

        return {
            status: SEND_REQUEST_STATUS.ERROR,
            error: error.message,
            duration,
            retries: attempt + 1,
            response: null,
        }
    } finally {
        clearTimeout(timeoutId)
    }
}

export const resetCache = async () => {
    // console.log('Limpiando caché...', cache)
    cache.clear()
    console.log('Caché limpiada.', cache)
}

/**
 * @description Borra todas las claves que incluyan un fragmento (por ejemplo, una URL base).
 * @param keyFragment Fragmento de la clave (por ejemplo, parte de la URL).
 */
export const removeCacheByFragment = (keyFragment: string) => {
    let count = 0
    for (const key of cache.keys()) {
        if (key.includes(keyFragment)) {
            cache.delete(key)
            count++
        }
    }
    console.log(`Cache borrada para ${count} claves que contienen: ${keyFragment}`)
}